import { SelectItem } from 'src/interfaces/form/select';
import { useManufacturers } from '../api/requestConfigs/manufacturers';
import { useGetCountries, useGetOrderReferenceData, useGetProductReferenceData, useGetReturnOrderReferenceData, useGetSupplierReferenceData } from '../api/requestConfigs/products';
import { KeyValuePair } from 'src/interfaces/bold/product/product';
import formHelper from 'src/utils/formHelper';
import { useGetPurchaseOrderReferenceData, useLowOrderPointsReferenceData, useSuppliers } from '../api/requestConfigs/purchasing';
import { Supplier } from 'src/interfaces/bold/product/supplier';
import { useGetCategories } from '../api/requestConfigs/salesitems';
import { mapCategoriesToTree } from '../table/configs/useCategoriesTable';
import { useTransferOrderReferenceData } from '../api/requestConfigs/warehouse';
import { useCurrencies } from '../api/requestConfigs/admin';
import { useCampaignReferenceData, useCustomerReferenceData } from '../api/requestConfigs/orders';
import { isServerAtLeast } from 'src/utils/enviromentValues';


const mapSubClassOptions = (productClasses, productSubClasses) => {
  let productSubClassOptions = {}
  productClasses?.forEach(productClass => {
    productSubClassOptions[productClass?.Key] = [{ value: null, label: "-" }, ...formHelper.mapToOption<any>(productSubClasses?.filter(subClass => subClass.ParentId == productClass?.Key) ?? [], null)];
  });
  return productSubClassOptions;
};


export const useManufacturerOptions = () => {
  const { data: items } = useManufacturers();
  const options = formHelper.mapToOption<KeyValuePair>(items, "Manufacturer");
  return options;
};

export const useCurrencyOptions = () => {
  const items = useCurrencies();
  const options = formHelper.mapToOption<KeyValuePair>(items, ["Currency", "Key"], ["Currency", "Value"]);
  return options;
}


export const useCategoriesOptions = () => {
  const items = mapCategoriesToTree(useGetCategories());
  const options = formHelper.mapToOption<any>(items, "CategoryId", "path");
  return options;
};


const useProductOptions = (): [productTypeOptions: any[], productClassOptions: any[], productSubClassOptions: {}, manufacturerOptions: SelectItem[], productLinesOptions: any[], manufacturerOptions: SelectItem[]] => {
  const [referenceData] = useGetProductReferenceData();
  return [
    formHelper.mapToOption<any>(referenceData?.ProductTypes ?? []),
    formHelper.mapToOption<any>(referenceData?.ProductClasses ?? []),
    mapSubClassOptions(referenceData?.ProductClasses, referenceData?.ProductSubClasses),
    formHelper.mapToOption<any>(referenceData?.Manufacturers ?? []),
    formHelper.mapToOption<any>(referenceData?.ProductLines ?? []),
    formHelper.mapToOption<any>(referenceData?.CountriesOfOrigin ?? []),
  ];
};
export const useSalesItemOptions = (): [productTypeOptions: any[]] => {
  const [referenceData] = useGetProductReferenceData();
  return [formHelper.mapToOption<any>(referenceData?.ProductTypes ?? [])];
};



export const useSupplierProductOptions = (): [supplierOptions: SelectItem[], manufacturerOptions: SelectItem[], currencyOptions: any[]] => {
  const { rows: items } = useSuppliers();
  const supplierOptions = formHelper.mapToOption<Supplier>(items, "Id", "Name");
  const manufacturerOptions = useManufacturerOptions();
  const currencyOptions = useCurrencyOptions();
  return [supplierOptions, manufacturerOptions, currencyOptions];
};

export const useSupplierOptions = () => {
  const [referenceData] = useGetSupplierReferenceData();
  const countries = useGetCountries();
  const countriesRef = formHelper.mapToOption<any>(countries ?? [], 'Id', 'Iso3166Name');
  const shippingMethods = referenceData?.ShippingMethods ?? [];
  const shippingMethodsNonDropShip = shippingMethods.filter((m: any) => m.PurchaseOrderTypeAllowed === 1);
  const shippingMethodsDropShip = shippingMethods.filter((m: any) => m.PurchaseOrderTypeAllowed === 2);
  return [
    formHelper.mapToOption<any>(referenceData?.Currencies ?? []),
    formHelper.mapToOption<any>(shippingMethodsNonDropShip ?? [], "Id", "Name"),
    formHelper.mapToOption<any>(shippingMethodsDropShip ?? [], "Id", "Name"),
    formHelper.mapToOption<any>(referenceData?.ShippingTerms ?? []),
    formHelper.mapToOption<any>(referenceData?.PaymentMethods ?? []),
    formHelper.mapToOption<any>(referenceData?.PaymentTerms ?? []),
    formHelper.mapToOption<any>(referenceData?.OrderSendTypes ?? []),
    countriesRef
  ];
};


export const useOrderOptions = (): [
  shippingMethodOptions: any[],
  shippingTermsOptions: any[],
  shippingRecieverOptions: any[],
  salesOrganizationOptions: any[],
  paymentMethodOptions: any[],
  paymentTermOptions: any[],
  discountTypeOptions: any[],
  discountCategoryOptions: any[],
  priorityOptions: any[],
  currencyOptions: any[],
  warehouseOptions: any[],
  fulfillmentMethodOptions: any[]
] => {
  const [referenceData] = useGetOrderReferenceData();
  const [transferOrderReferenceData] = useTransferOrderReferenceData();

  //console.log("Order reference data", referenceData);
  const currencyOptions = useCurrencyOptions();

  const fallbackFulfillmentMethods = [
    {
      Key: 1,
      Value: "ByFulfillmentRules",
    },
    {
      Key: 2,
      Value: "FromRequiredWarehouse",
    },
  ];

  const fulfillmentMethodReferenceData =
    Array.isArray(referenceData?.FulfillmentMethods) && referenceData.FulfillmentMethods.length
      ? referenceData.FulfillmentMethods
      : fallbackFulfillmentMethods;

  // Change this to referenceData?.Warehouses when/if warehouses are added to order reference data.
  const warehouseReferenceData =
    Array.isArray(referenceData?.Warehouses) && referenceData.Warehouses.length
      ? referenceData.Warehouses
      : transferOrderReferenceData?.Warehouses ?? [];

  return [
    isServerAtLeast(2)
      ? formHelper.mapToOption<any>(referenceData?.ShippingMethods ?? [], "Id", "Name")
      : formHelper.mapToOption<any>(referenceData?.ShippingMethods ?? [], "_Id", "_Name"), // TEMP FIX for server < 2
    formHelper.mapToOption<any>(referenceData?.ShippingTerms ?? []),
    formHelper.mapToOption<any>(referenceData?.ShippingReceivers ?? []),
    formHelper.mapToOption<any>(referenceData?.SalesOrganizations ?? []),
    formHelper.mapToOption<any>(referenceData?.PaymentMethods ?? []),
    formHelper.mapToOption<any>(referenceData?.PaymentTerms ?? []),
    formHelper.mapToOption<any>(referenceData?.DiscountTypes ?? []),
    formHelper.mapToOption<any>(referenceData?.DiscountCategories ?? []),
    formHelper.mapToOption<any>(referenceData?.OrderPriority ?? []),
    currencyOptions,
    formHelper.mapToOption<any>(warehouseReferenceData),
    formHelper.mapToOption<any>(fulfillmentMethodReferenceData),
  ];
};

export const useReturnOrderOptions = (): [ReturnUnits: any[], ReturnReasons: any[]] => {
  const [referenceData] = useGetReturnOrderReferenceData();
  return [
    formHelper.mapToOption<any>(referenceData?.ReturnUnits ?? []),
    formHelper.mapToOption<any>(referenceData?.ReturnReasons ?? []),
  ];
};
export const useLowOrderPointsOptions = (): [WarehouseList: any[], DocumentTypes: any[], DeliveryMethods: any[], Status: any[]] => {
  const [referenceData] = useLowOrderPointsReferenceData();
  console.log("Low Order Points Reference Data", referenceData);
  return [
    formHelper.mapToOption<any>(referenceData?.WarehouseList ?? []),
    formHelper.mapToOption<any>(referenceData?.DocumentTypes ?? []),
    formHelper.mapToOption<any>(referenceData?.DeliveryMethods ?? []),
    formHelper.mapToOption<any>(referenceData?.LowOrderPointStatuses ?? []),
  ];
};


export const useTransferOrderOptions = (): [DeliveryMethods: any[], Warehouse: any[]] => {
  const [referenceData] = useTransferOrderReferenceData();
  return [
    formHelper.mapToOption<any>(referenceData?.DeliveryMethods ?? []),
    //Warehouses
    formHelper.mapToOption<any>(referenceData?.Warehouses ?? []),
  ];
};


export const useCustomerOptions = () => {
  const { rows: data } = useCustomerReferenceData();

  const currencyOptions = formHelper.mapToOption<any>(data?.Currencies ?? []);
  const categoryOptions = formHelper.mapToOption<any>(data?.Categories ?? []);
  const discountCategoryOptions = formHelper.mapToOption<any>(data?.DiscountCategories ?? []);
  const shippingMethodOptions = formHelper.mapToOption<any>(data?.ShippingMethods ?? []);
  const shippingTermsOptions = formHelper.mapToOption<any>(data?.ShippingTerms ?? []);
  const paymentMethodOptions = formHelper.mapToOption<any>(data?.PaymentMethods ?? []);
  const paymentTermsOptions = formHelper.mapToOption<any>(data?.PaymentTerms ?? []);
  const salesPriceListOptions = formHelper.mapToOption<any>(data?.SalesPriceLists ?? []);
  const salesChannelOptions = formHelper.mapToOption<any>(data?.SalesChannels ?? []);
  const customerTypeOptions = formHelper.mapToOption<any>(data?.CustomerTypes ?? []);
  const activeStatusOptions = formHelper.mapToOption<any>(data?.ActiveStatus ?? []);

  return [
    currencyOptions,
    categoryOptions,
    discountCategoryOptions,
    shippingMethodOptions,
    shippingTermsOptions,
    paymentMethodOptions,
    paymentTermsOptions,
    salesPriceListOptions,
    salesChannelOptions,
    customerTypeOptions,
    activeStatusOptions,
    data
  ];
};




export const useCampaignOptions = (): [
  CampaignTypes: any[],
  CampaignStatuses: any[],
  SalesPriceLists: any[],
  DiscountTypes: any[],
  RawSalesPriceLists: any[],
] => {
  const referenceData: any = useCampaignReferenceData();
  const rawSalesPriceLists = referenceData?.SalesPriceLists ?? [];

  return [
    formHelper.mapToOption<any>(referenceData?.CampaignTypes ?? [], 'Id', 'Name'),
    formHelper.mapToOption<any>(referenceData?.CampaignStatuses ?? [], 'Id', 'Name'),
    formHelper.mapToOption<any>(rawSalesPriceLists, 'Id', 'Name'),
    formHelper.mapToOption<any>(referenceData?.DiscountTypes ?? [], 'Id', 'Name'),
    rawSalesPriceLists,
  ];
};



type RecipientOption = SelectItem & {
  warehouseTypeId?: number;
  warehouseTypeName?: string;
};

export const usePurchaseOrderOptions = (): [

  purchaseOrderStatus: any[],
  baseCurrency: any[],
  baseCurrencyExchangeRates: any[],
  currencies: any[],
  recipients: RecipientOption[],        // ⬅️ enriched with warehouse type
  paymentTerms: any[],
  shippingTerms: any[],
  paymentMethods: any[],
  shippingMethods: any[],
  sendTypes: any[],
  countries: any[],
  suppliers: SelectItem[],

] => {
  const [referenceData] = useGetPurchaseOrderReferenceData();
  const countries = useGetCountries();
  const countriesRef = formHelper.mapToOption<any>(countries ?? [], "Id", "Iso3166Name");
  const { rows: suppliers } = useSuppliers();

  // ⬇️ Map recipients as before (value/label), then enrich with type from the raw payload
  const rawRecipients = referenceData?.Recipients ?? [];
  const baseRecipientOptions = formHelper.mapToOption<any>(rawRecipients, "WarehouseId", "WarehouseName");

  const recipients: RecipientOption[] = baseRecipientOptions.map(opt => {
    const src = rawRecipients.find((r: any) => r?.WarehouseId === opt.value);
    return {
      ...opt,
      warehouseTypeId: src?.WarehouseType?.Id,
      warehouseTypeName: src?.WarehouseType?.Name
    };
  });

  return [
    formHelper.mapToOption<any>(referenceData?.PurchaseOrderStatus ?? []),
    formHelper.mapToOption<any>(referenceData?.BaseCurrency ?? []),
    formHelper.mapToOption<any>(referenceData?.BaseCurrencyExchangeRates ?? []),
    formHelper.mapToOption<any>(referenceData?.Currencies ?? []),
    recipients, // ⬅️ with warehouseTypeId/Name
    formHelper.mapToOption<any>(referenceData?.PaymentTerms ?? []),
    formHelper.mapToOption<any>(referenceData?.ShippingTerms ?? []),
    formHelper.mapToOption<any>(referenceData?.PaymentMethods ?? []),
    formHelper.mapToOption<any>(referenceData?.ShippingMethods ?? []),
    formHelper.mapToOption<any>(referenceData?.SendTypes ?? []),
    countriesRef,
    formHelper.mapToOption<any>(suppliers, "Id", "Name"),
  ];
};




export default useProductOptions;